import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { toursService } from "api/services/tours.service";
import ContentHeader from "components/Layout/ContentHeader";
import ProductOrderCancelModal from "components/order-management/ProductOrderCancelModal";
import Badge from "components/UI/Badge";
import Button from "components/UI/Button";
import { Input } from "components/UI/Input";
import Modal from "components/UI/Modal";
import Pagination from "components/UI/Pagination";
import Table, { Column } from "components/UI/Table";
import {
  MEMBER_DEEP_LINK_PARAM_PUBLIC_ID,
  MEMBER_DEEP_LINK_STATE_LEGACY_USER_ID,
} from "utils/member-search";

import type { AdminOrderCancelTarget } from "api/types/order.types";
import type { TourListItem, TourParticipant } from "api/types/tours.types";
import "pages/AdminPage.scss";
import "./TourManagement.scss";

const formatPrice = (v: string | number | null) =>
  v != null ? `${Number(v).toLocaleString()}원` : "-";

const formatDate = (v: string | null) => (v ? v.slice(0, 10) : "-");

// 백엔드 TourListView가 페이지 크기를 12로 고정(page_size 파라미터 무시)
const PAGE_SIZE = 12;

const TourManagement: React.FC = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<TourListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedOrder, setSelectedOrder] = useState<AdminOrderCancelTarget | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);

  // 참가자 모달
  const [participantsModal, setParticipantsModal] = useState<{
    open: boolean;
    tourId: number | null;
    tourName: string;
    participants: TourParticipant[];
  }>({ open: false, tourId: null, tourName: "", participants: [] });

  const fetchList = async (page: number) => {
    setLoading(true);
    try {
      const res = await toursService.list({
        page,
        location: search || undefined,
      });
      if (Array.isArray(res.data)) {
        setItems(res.data);
        setTotal(res.data.length);
      } else {
        setItems(res.data.results ?? []);
        setTotal(res.data.count ?? 0);
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  const handleSearch = () => {
    if (currentPage === 1) {
      fetchList(1);
    } else {
      setCurrentPage(1);
    }
  };

  const openParticipants = async (tour: Pick<TourListItem, "id" | "name">) => {
    try {
      const res = await toursService.participants(tour.id);
      setParticipantsModal({
        open: true,
        tourId: tour.id,
        tourName: tour.name,
        participants: res.data.participants ?? res.data ?? [],
      });
    } catch {
      // ignore
    }
  };

  // 환불 완료 후 참가자 상태(취소선)와 투어 목록의 참가/정원을 함께 갱신
  const handleRefunded = () => {
    if (participantsModal.tourId != null) {
      openParticipants({ id: participantsModal.tourId, name: participantsModal.tourName });
    }
    fetchList(currentPage);
  };

  const columns: Column<TourListItem>[] = [
    { key: "name", title: "투어명", width: "220px" },
    { key: "location", title: "장소", width: "120px" },
    {
      key: "start_date",
      title: "기간",
      width: "180px",
      render: (_, row) => {
        if (!row.start_date && !row.end_date) return "-";
        return `${formatDate(row.start_date)} ~ ${formatDate(row.end_date)}`;
      },
    },
    { key: "price", title: "가격", width: "100px", render: (v) => formatPrice(v as string) },
    {
      key: "guide_names",
      title: "인솔강사",
      width: "120px",
      render: (v) => {
        const names = v as string[];
        return names.length > 0 ? names.join(", ") : "-";
      },
    },
    {
      key: "confirmed_count",
      title: "참가/정원",
      width: "90px",
      render: (v, row) => `${v}/${row.max_participants}`,
    },
    {
      key: "tour_type",
      title: "유형",
      width: "80px",
      render: (v) => (
        <Badge variant={String(v) === "LIMITED" ? "info" : "draft"}>
          {String(v) === "LIMITED" ? "기간제" : "상시"}
        </Badge>
      ),
    },
    {
      key: "actions",
      title: "참가자",
      width: "160px",
      render: (_, row) => (
        <div className="tour-actions" onClick={(e) => e.stopPropagation()}>
          <Button size="sm" variant="outline" onClick={() => openParticipants(row)}>
            보기
          </Button>
        </div>
      ),
    },
  ];

  const participantColumns: Column<TourParticipant>[] = [
    {
      key: "user",
      title: "이름",
      width: "80px",
      render: (_, row) => row.user?.name ?? "-",
    },
    {
      key: "payment_date",
      title: "결제 날짜",
      width: "110px",
      render: (_, row) => formatDate(row.created_at ?? null) ?? "-",
    },
    {
      key: "gender",
      title: "성별",
      render: (_, row) => row.gender ?? "-",
    },
    {
      key: "user",
      title: "연락처",
      width: "110px",
      render: (_, row) => row.user?.hp ?? "-",
    },
    {
      key: "user_id",
      title: "아이디",
      width: "120px",
      render: (_, row) => {
        const userId = row.user?.user_id ?? "";
        // 탈퇴 회원 참가자는 BE 가 `{'id': None, 'user_id': None, ...}` 으로 내려준다.
        // 종전 `?? ""` 는 그 경우에도 링크를 걸어 `?id=`(빈 값) 으로 이동시켰다 — 관리자가
        // "이 참가자"를 눌렀는데 무관한 전체 목록이 떴다. 식별자가 없으면 링크를 만들지 않는다.
        if (!userId) return "-";
        // 값의 **종류를 명시**해서 싣는다 — 소비자가 형식으로 추측하면 `user_id` 가 UUID
        // 모양인 회원과 그 문자열을 `public_id` 로 가진 다른 회원이 뒤바뀐다.
        // `public_id` 가 있으면 그것으로 딥링크한다 (검색 왕복 없이 상세로).
        // `public_id` 가 있으면 typed query 로 직행한다. 없으면 legacy `user_id` 를
        // **URL 이 아니라** navigate state 로 넘긴다 — 로그인 아이디를 주소창·히스토리·
        // Referer·access log 에 남기지 않는다.
        const publicId = row.user?.public_id;
        const openMember = () => {
          if (publicId) {
            navigate(
              `/user-list?${MEMBER_DEEP_LINK_PARAM_PUBLIC_ID}=${encodeURIComponent(publicId)}`,
            );
            return;
          }
          navigate("/user-list", { state: { [MEMBER_DEEP_LINK_STATE_LEGACY_USER_ID]: userId } });
        };
        return (
          <span
            style={{ color: "rgb(99, 102, 241)", textDecoration: "underline", cursor: "pointer" }}
            onClick={openMember}
          >
            {userId}
          </span>
        );
      },
    },
    {
      key: "license",
      title: "자격증",
      render: (_, row) => <span>{row.license ?? "-"}</span>,
    },
    {
      key: "companions",
      title: "동반인원",
      width: "80px",
      render: (v) =>
        `${v?.length ?? 0}명 ${v?.length > 0 ? `(${v?.map((companion: { name: string }) => companion.name).join(", ")})` : ""}`,
    },
    {
      key: "refund",
      title: "",
      width: "80px",
      render: (_, row) => {
        // 참가자 id = 주문 id (BE TourParticipantsView). 이미 취소된 주문은 BE 가 400 으로 거부한다.
        const orderId = row.id;
        if (orderId == null || row.status === "CANCELLED") return null;
        return (
          <Button
            size="sm"
            variant="outline"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedOrder({ id: orderId, total_amount: row.total_amount, product_type: "TRAVEL" });
              setShowCancelModal(true);
            }}
          >
            환불
          </Button>
        );
      },
    },
  ];

  return (
    <div className="admin-page">
      <ContentHeader
        title="투어 관리"
        description="투어를 관리하고 출발일정 및 참가자를 확인합니다."
      />

      <div className="admin-page__card">
        <div className="admin-page__filter">
          <Input
            placeholder="투어 장소 검색"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            prefix="🔍"
          />
          <Button variant="secondary" size="sm" onClick={handleSearch}>
            조회
          </Button>
        </div>

        {loading ? (
          <div className="admin-page__loading">불러오는 중...</div>
        ) : (
          <Table
            columns={columns}
            data={items}
            rowKey="id"
            onRowClick={(row) => navigate(`/tour-edit/${row.id}?source=product`)}
          />
        )}
        <Pagination
          current={currentPage}
          total={total}
          pageSize={PAGE_SIZE}
          onChange={setCurrentPage}
        />
      </div>

      {/* 참가자 모달 */}
      <Modal
        open={participantsModal.open}
        title={`참가자 목록 — ${participantsModal.tourName}`}
        width="800px"
        onClose={() =>
          setParticipantsModal({ open: false, tourId: null, tourName: "", participants: [] })
        }
      >
        {participantsModal.participants.length === 0 ? (
          <div className="admin-page__loading">참가자가 없습니다.</div>
        ) : (
          <Table
            columns={participantColumns}
            data={participantsModal.participants}
            rowKey={(row) => String(row.id ?? row.user?.name)}
            rowClassName={(row) => (row.status === "CANCELLED" ? "participant-row--cancelled" : "")}
          />
        )}
      </Modal>

      {/* 환불(주문 취소) 모달 — 참가자 모달 위에 겹치도록 뒤에 렌더 */}
      <ProductOrderCancelModal
        open={showCancelModal}
        order={selectedOrder}
        onClose={() => setShowCancelModal(false)}
        onCancelled={handleRefunded}
      />
    </div>
  );
};

export default TourManagement;
